import React, { useState, useEffect } from 'react';
import axios from '../api/axiosConfig';
import './Modal.css';

const RegisterCUPLModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    cupl_number: '',
    plate: '',
    owner_name: '',
    tramite_type_id: '',
    expiration_date: '',
    total_amount: '',
  });
  const [tramiteTypes, setTramiteTypes] = useState([]);
  const [error, setError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const token = localStorage.getItem('token');

  // Cargar tipos de trámites
  useEffect(() => {
    const fetchTramiteTypes = async () => {
      try {
        // Obtener info del usuario para saber su tránsito
        const userRes = await axios.get('/api/auth/me');
        const userTransitoId = userRes.data.transito_id;

        const response = await axios.get('/api/tramite-types');
        
        // Filtrar trámites por el tránsito del usuario (si no es superadmin)
        if (userRes.data.role !== 'superadmin' && userTransitoId) {
          setTramiteTypes(response.data.filter(tt => tt.transito_id === userTransitoId));
        } else {
          setTramiteTypes(response.data);
        }
      } catch (error) {
        console.error('Error al obtener tipos de trámites:', error);
      }
    };

    if (isOpen) {
      fetchTramiteTypes();
    }
  }, [isOpen, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Por favor, selecciona un archivo PDF');
      return;
    }

    setExtracting(true);
    setError('');
    
    const uploadFormData = new FormData();
    uploadFormData.append('pdf', file);

    try {
      const response = await axios.post('/api/automation/extract-pdf', uploadFormData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
      });

      if (response.data.success) {
        const { cupl_number, plate, owner_name, tramite_type_id, expiration_date, total_amount } = response.data.data;
        setFormData({
          cupl_number: cupl_number || '',
          plate: plate || '',
          owner_name: owner_name || '',
          tramite_type_id: tramite_type_id || '',
          expiration_date: expiration_date || '',
          total_amount: total_amount || '',
        });
      }
    } catch (err) {
      console.error('Error al extraer PDF:', err);
      setError(err.response?.data?.message || 'Error al procesar el archivo PDF');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.cupl_number) {
      setError('El número de CUPL es obligatorio');
      return;
    }

    onSubmit(formData);
    setFormData({
      cupl_number: '',
      plate: '',
      owner_name: '',
      tramite_type_id: '',
      expiration_date: '',
      total_amount: '',
    });
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => !loading && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📝 Registrar Nuevo CUPL</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group file-upload-group" style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '2px dashed #e2e8f0', marginBottom: '20px' }}>
            <label htmlFor="pdf-upload" style={{ color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              📄 {extracting ? 'Extrayendo datos...' : 'Subir factura PDF para autocompletar'}
            </label>
            <input
              type="file"
              id="pdf-upload"
              accept=".pdf"
              onChange={handleFileUpload}
              disabled={loading || extracting}
              style={{ marginTop: '10px', fontSize: '13px' }}
            />
            {extracting && <div className="loading-spinner-small" style={{ marginTop: '5px', fontSize: '12px', color: '#64748b' }}>Procesando archivo...</div>}
          </div>

          <div className="form-group">
            <label htmlFor="cupl_number">Número CUPL:</label>
            <input
              type="text"
              id="cupl_number"
              name="cupl_number"
              value={formData.cupl_number}
              onChange={handleChange}
              placeholder="Ej: CUPL-2024-001"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="plate">Placa:</label>
            <input
              type="text"
              id="plate"
              name="plate"
              value={formData.plate}
              onChange={handleChange}
              placeholder="Ej: ABC-1234"
              disabled={loading}
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="owner_name">Propietario:</label>
            <input
              type="text"
              id="owner_name"
              name="owner_name"
              value={formData.owner_name}
              onChange={handleChange}
              placeholder="Nombre completo"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="expiration_date">Fecha Límite de Pago:</label>
            <input
              type="text"
              id="expiration_date"
              name="expiration_date"
              value={formData.expiration_date}
              onChange={handleChange}
              placeholder="DD/MM/YY"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="total_amount">Valor a Pagar ($):</label>
            <input
              type="number"
              id="total_amount"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              placeholder="Ej: 90000"
              disabled={loading}
            />
            <small style={{ color: '#64748b', fontSize: '11px' }}>
              * Si se deja vacío, se usará el valor del Tipo de Trámite.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="tramite_type_id">Tipo de Trámite:</label>
            <select
              id="tramite_type_id"
              name="tramite_type_id"
              value={formData.tramite_type_id}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="">Seleccionar trámite...</option>
              {tramiteTypes.map(tramite => (
                <option key={tramite.id} value={tramite.id}>
                  {tramite.name} (${tramite.value.toLocaleString('es-CO')})
                </option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading || extracting}
            >
              {loading ? '⏳ Registrando...' : extracting ? '⏳ Procesando...' : '✓ Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterCUPLModal;
